import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ROLE_LABELS } from "@easyshop/shared";
import { Avatar, cx } from "@/components/ui";
import { DesktopPageFrame, IosBadge, IosGroup, IosRow, IosSection, MobileIcon, type IconName } from "@/components/mobile/MobileUi";
import { useBoutiqueActivity } from "@/layouts/MobileShell";
import { logout } from "@/layouts/HeaderControls";
import { useSession } from "@/hooks/useSession";
import { useMyPlanning } from "@/lib/queries";
import { switchOrganization } from "@/lib/organization";
import { ymd } from "@/components/planning/utils";
import { initials } from "@/lib/format";
import { AccessPage } from "@/routes/Access";
import { ProfilePage } from "@/routes/Profile";

/** Icône carrée façon Réglages iOS (fond accent, glyphe blanc). */
function SquareIcon({ icon }: { icon: IconName }) {
  return (
    <span className="flex h-[29px] w-[29px] flex-shrink-0 items-center justify-center rounded-[7px] bg-accent">
      <MobileIcon name={icon} size={17} color="#fff" strokeWidth={2} />
    </span>
  );
}

/** Onglet « Plus » : planning, pages secondaires, réseaux, déconnexion. */
export function MobilePlus() {
  const { me, can } = useSession();
  const navigate = useNavigate();
  const { openTasks, lateTasks } = useBoutiqueActivity();
  const mine = useMyPlanning();
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const role = me.organizations.find((o) => o.id === me.organization?.id)?.viaPlatform
    ? "Administrateur plateforme"
    : me.role
      ? ROLE_LABELS[me.role]
      : "";

  // Horaire du jour à droite de « Planning » (si la personne est au planning).
  const today = ymd(new Date());
  const shift = (mine.data?.shifts ?? []).find((s) => s.date === today);
  const hasStaff = (mine.data?.staffMemberIds.length ?? 0) > 0;

  const pages: { to: string; icon: IconName; label: string; show: boolean; right?: ReactNode }[] = [
    {
      to: "/m/planning",
      icon: "calendar",
      label: "Planning",
      show: can("planning.read"),
      right: hasStaff ? <span className="flex-shrink-0 text-[15px] tabular text-muted">{shift ? `${shift.start}–${shift.end}` : "Repos"}</span> : undefined,
    },
    { to: "/m/taches", icon: "check", label: "Tâches", show: can("messages.read"), right: <IosBadge n={lateTasks || openTasks} /> },
    { to: "/m/promos", icon: "tag", label: "Promos & campagnes", show: can("campaigns.read") },
    { to: "/m/arrivages", icon: "truck", label: "Arrivages", show: can("arrivals.read") },
    { to: "/m/acces", icon: "users", label: "Accès & équipe", show: can("members.read") },
    // Pas de version mobile : la page Paramètres s'ouvre dans la vue PC adaptée.
    { to: "/parametres", icon: "key", label: "Comptes Hiboutik & boutiques", show: can("boutiques.manage") },
    { to: "/m/it", icon: "code", label: "IT · suivi des livraisons", show: Boolean(me.organization?.itEnabled) && can("it.manage") },
    { to: "/m/profil", icon: "user", label: "Profil", show: true },
  ];

  const switchTo = async (id: string) => {
    setSwitching(id);
    setError(null);
    try {
      await switchOrganization(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Changement de réseau impossible");
      setSwitching(null);
    }
  };

  return (
    <>
      <div className="flex items-center gap-[14px] rounded-ios bg-white px-4 py-3">
        <Avatar text={initials(me.user.name)} className="!h-14 !w-14 !text-[22px] !font-semibold" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[20px] font-semibold">{me.user.name}</div>
          <div className="truncate text-[15px] text-muted">
            {role}
            {me.organization ? ` · ${me.organization.name}` : ""}
          </div>
        </div>
      </div>

      <IosGroup>
        {pages
          .filter((p) => p.show)
          .map((p) => (
            <IosRow key={p.to} to={p.to} left={<SquareIcon icon={p.icon} />} right={p.right}>
              <span className="block truncate text-[16px]">{p.label}</span>
            </IosRow>
          ))}
      </IosGroup>

      {(me.organizations.length > 1 || me.platformAdmin) && (
        <IosSection title="Réseaux">
          <IosGroup>
            {me.organizations.map((o) => {
              const current = o.id === me.organization?.id;
              return (
                <IosRow
                  key={o.id}
                  onClick={() => void switchTo(o.id)}
                  disabled={current || switching != null}
                  chevron={false}
                  left={
                    <span className="flex h-[29px] w-[29px] flex-shrink-0 items-center justify-center rounded-[7px] bg-accent-soft text-[12px] font-bold text-accent">
                      {initials(o.name)}
                    </span>
                  }
                  right={<span className="flex-shrink-0 text-[15px] text-accent">{current ? "✓" : switching === o.id ? "…" : "Basculer"}</span>}
                >
                  <span className={cx("block truncate text-[16px]", current && "text-accent")}>{o.name}</span>
                  <span className="block truncate text-[13px] text-muted">{o.viaPlatform ? "Accès plateforme" : ROLE_LABELS[o.role]}</span>
                </IosRow>
              );
            })}
            {me.platformAdmin && (
              // Pas d'équivalent mobile : la page plateforme s'ouvre dans la vue PC adaptée.
              <IosRow onClick={() => navigate("/reseaux")} left={<SquareIcon icon="globe" />} right={<IosBadge n={me.pendingApprovals} />}>
                <span className="block truncate text-[16px]">Tous les réseaux</span>
              </IosRow>
            )}
          </IosGroup>
          {error && <div className="px-4 pt-2 text-[13px] font-semibold text-danger">{error}</div>}
        </IosSection>
      )}

      <IosGroup>
        <button type="button" onClick={() => void logout()} className="flex min-h-[44px] w-full items-center justify-center bg-white text-[17px] text-ios-badge">
          Se déconnecter
        </button>
      </IosGroup>
    </>
  );
}

// Pages PC affichées dans le shell mobile (le retour « ‹ Plus » est dans la barre de navigation).
export function MobileAccess() {
  const { can } = useSession();
  if (!can("members.read")) return null;
  return (
    <DesktopPageFrame>
      <AccessPage />
    </DesktopPageFrame>
  );
}

export function MobileProfile() {
  return (
    <DesktopPageFrame>
      <ProfilePage />
    </DesktopPageFrame>
  );
}
